<template>
  <div class="notefie-dashboard-content">
    <nav>
      <div class="top-nav-bar" :class="navigationBarColor">
        <div class="user-info">
          <n-flex class="user-greeting">
            <span class="greeting-text hidden md:block">Hello</span>
            <span class="user-name hidden md:block mr-6" v-if="authStore.userAttributes">
              {{ authStore.userAttributes.given_name }}
              {{ authStore.userAttributes.family_name }}!
            </span>
            <span class="user-name" v-else>stranger!</span>
          </n-flex>
          <n-flex>
            <div class="organisation-and-subscription">
              <div v-if="showOrganisationLabel" class="organisation-info">
                <span class="org-label hidden md:block">Account:</span>
              </div>
            </div>
            <div v-if="moreMQTTAccounts" class="nav-account-control">
              <n-select
                v-model:value="selectedAccountValue"
                :options="selectNotefieID"
                :on-update:value="selectedAccountCallback"
                class="notefieID-selection"
              />
            </div>
            <div v-else class="nav-account-control single-notieID">
              {{ singleMQTTAccount }}
            </div>

            <div
              v-if="showNavSubscriptionInfo && (subscriptionTier || subscriptionDisplay)"
              class="subscription-info"
            >
              <span class="subscription-pill">
                <span class="subscription-tier" v-if="subscriptionTier">
                  Tier: {{ subscriptionTier }}
                </span>
                <span v-if="subscriptionDisplay">• {{ subscriptionDisplay }}</span>
              </span>
            </div>
          </n-flex>
        </div>

        <div class="notefie-dashboard__buttons">
          <ul>
            <li class="logout-button" @click="logOut">
              <sign-out-alt class="logout-icon" />
              <span class="logout-text">Logout</span>
            </li>
            <li><menu-sharp class="menu" @click="toggleSideNav" /></li>
          </ul>
        </div>
      </div>
    </nav>
  </div>
</template>

<script setup lang="ts">
import router from './../router/index'
import { useAuthStore } from './../stores/auth'
import { useLayoutStore } from './../stores/layout'
import { signOut } from 'aws-amplify/auth'
import { SignOutAlt } from '@vicons/fa'
import { MenuSharp } from '@vicons/ionicons5'
import { ref, onMounted, computed } from 'vue'
import { useNotefieIdStore } from './../stores/notefieID'
import { notefieIDsService } from '../services/notefieIDs.service'
import { getSubscriptionInfo, isSubscriptionAccessDenied } from '../utils/subscriptionUtils'
import { getTierDisplayName } from '../config/subscriptionTiers'
import { clearSessionActivity } from '../utils/sessionIdle'
const notefieIdStore = useNotefieIdStore()
const authStore = useAuthStore()
const layoutStore = useLayoutStore()
var selectedAccountValue = ref()
var moreMQTTAccounts = ref(false)
var singleMQTTAccount = ref('')
var selectNotefieID = <Array<any>>[] //dropdown menu options to seelct ID
const navigationBarColor = ref('navigationBarNoColor')

//to set color of top bar based on which environmenet is loaded
const envMode = import.meta.env.MODE
//console.log("Environment " + envMode)
if (envMode == 'development' || envMode == 'dev') {
  navigationBarColor.value = 'navigationBarGreen'
} else if (envMode == 'staging') {
  navigationBarColor.value = 'navigationBarOrange'
} else {
  navigationBarColor.value = 'navigationBarNoColor'
}

const subscriptionInfo = computed(() => getSubscriptionInfo(authStore.subscriptionStatus))
const subscriptionTier = computed(() => {
  return getTierDisplayName(subscriptionInfo.value.tier)
})
const subscriptionDisplay = computed(() => {
  return subscriptionInfo.value.display
})
const showNavSubscriptionInfo = computed(() => {
  const info = subscriptionInfo.value
  if (isSubscriptionAccessDenied(authStore.subscriptionStatus)) {
    return true
  }
  if (info.subscriptionValidTo != null && info.subscriptionValidTo !== '' && info.daysLeft <= 30) {
    return true
  }
  return false
})
const showOrganisationLabel = computed(
  () =>
    moreMQTTAccounts.value ||
    !!(singleMQTTAccount.value && String(singleMQTTAccount.value).length > 0)
)
onMounted(async () => {
  if (authStore.authenticator?.authStatus === 'authenticated') {
    loadNotefieIDs()
  }
})
const loadNotefieIDs = async () => {
  try {
    const notefieIDsStorage: any = await notefieIDsService.getNotefieIDs()
    //const allNotefieIDsFromStorage: any = notefieIdStore.allNotefieIDs //load IDs from storage
    selectNotefieID = notefieIDsStorage.map((v: any) => ({
      label: v.label,
      value: v.value,
      siteLocation: v.siteLocation
    }))
    const fisrtIdvalue = selectNotefieID[0]
    const alreadySelectedNotefieID = notefieIdStore.siteLocationNotefieID
    if (alreadySelectedNotefieID.length > 1) {
      //if notefieID already selected before use existing one
      selectedAccountValue.value = alreadySelectedNotefieID
    } else {
      if (fisrtIdvalue) {
        selectedAccountValue.value = fisrtIdvalue.label
        singleMQTTAccount.value = fisrtIdvalue.label //don't print dropdown options for single account
      }
    }
    //show dropwown menu if more mqtt accounts
    if (selectNotefieID.length > 1) {
      moreMQTTAccounts.value = true
    } else {
      moreMQTTAccounts.value = false
      if (fisrtIdvalue) {
        singleMQTTAccount.value = fisrtIdvalue.label //don't print dropdown options for single account
      }
    }
  } catch (error) {}
}
const logOut = async () => {
  authStore.beginLogout()
  try {
    notefieIdStore.allNotefieIDs = []
    notefieIdStore.notefieID = ''
    notefieIdStore.siteLocationNotefieID = ''
    notefieIdStore.siteLocation = ''
    clearSessionActivity()
    await signOut()
    await router.push('login')
  } catch {
    authStore.finishLogout()
  }
}
const toggleSideNav = () => {
  layoutStore.toggleSideNav()
}
const selectedAccountCallback = (selectedNotefieID: string, object: any) => {
  selectedAccountValue.value = selectedNotefieID
  notefieIdStore.notefieID = selectedNotefieID
  notefieIdStore.siteLocation = object.siteLocation
  notefieIdStore.siteLocationNotefieID = object.label
}
</script>

<style lang="scss">
.container {
  width: 100%;
}

.top-nav-bar {
  @apply rounded-2xl mt-6 flex items-center ml-4 bg-white px-4 py-3 shadow-sm border border-gray-200;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

/* Mobile */
@media (max-width: 768px) {
  .top-nav-bar {
    flex-wrap: wrap;
  }

  .notefie-dashboard__buttons {
    width: 100%;
    display: flex;
    justify-content: flex-end;
    order: -1; // Move to top
    margin-bottom: 0.75rem;
  }

  .user-info {
    width: 100%;
  }

  .notefie-dashboard__buttons ul {
    margin: 0;
  }
}

.user-info {
  @apply flex items-center;
}

.user-greeting {
  @apply flex items-center gap-2;
}

.greeting-text {
  @apply text-gray-700 text-base font-medium;
}

.user-name {
  @apply text-gray-900 text-base font-semibold;
}

.organisation-info {
  @apply text-gray-700 text-base font-medium;
}

.organisation-and-subscription {
  @apply flex items-center gap-3;
}

.org-label {
  @apply text-base font-medium text-gray-700;
}

.subscription-info {
  @apply flex items-center;
}

.subscription-pill {
  @apply inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold;
  background-color: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.subscription-tier {
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.nav-account-control {
  margin-right: 5px;
  display: flex;
  align-items: center;
  min-height: 2.25rem;
  max-width: 90%;
  min-width: 0;
}

@media (max-width: 768px) {
  .nav-account-control {
    flex: 1 1 auto;
    min-width: 0;
    max-width: calc(100vw - 6rem);
    width: 100%;
    overflow: hidden;
  }
}

.single-notieID {
  @apply text-base font-semibold text-gray-900 leading-normal;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: calc(100vw - 6rem);
}

.notefieID-selection {
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow: hidden;
  --n-text-color: rgb(17 24 39);
  --n-placeholder-color: rgb(107 114 128);
  --n-border: 1px solid rgb(229 231 235);
  --n-border-hover: 1px solid rgb(209 213 219);
  --n-border-focus: 1px solid rgb(209 213 219);
  --n-border-active: 1px solid rgb(209 213 219);
  --n-border-radius: 0.375rem;
  --n-box-shadow-focus: 0 1px 2px 0 rgb(0 0 0 / 0.05);
  font-weight: 600;
}

.notefieID-selection :deep(.n-base-selection) {
  max-width: 100%;
  overflow: hidden;
}

.notefieID-selection :deep(.n-base-selection-label),
.notefieID-selection :deep(.n-base-selection-input),
.notefieID-selection :deep(.n-base-selection__render-label) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.notefieID-selection :deep(.n-base-selection-label) {
}

.logout-button {
  @apply flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 cursor-pointer;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: scale(1.05);

    .logout-icon {
      transform: scale(1.1);
    }

    .logout-text {
      transform: scale(1.05);
    }
  }
}

.logout-icon {
  @apply text-gray-900;
  transition: all 0.2s ease-in-out;
}

.logout-text {
  @apply text-sm font-medium text-gray-700;
  transition: all 0.2s ease-in-out;
}

.notefie-dashboard {
  &__buttons {
    @apply flex items-center;
    & ul {
      @apply flex items-center m-0;
      font-size: 14px;
      & li {
        @apply ml-2;
        & svg {
          @apply cursor-pointer;
          width: 25px;
        }
        & .menu {
          @include desktop {
            @apply hidden;
          }
        }
      }
    }
  }
}
</style>
